import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { fmtIST } from "@/shared/lib/time";
import {
  FileDown, FolderOpen, ChevronRight, ArrowLeft, Ruler, FileSpreadsheet,
  Download, Factory, CheckCircle2, Clock,
} from "lucide-react";
import { toast } from "sonner";

/* Production Files — scope users download final production files per wall,
   sub-scope wise, plus a neat Excel with measurements + file links. */

const fileSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const PF_STATUS = {
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  modify: "bg-orange-100 text-orange-800 border-orange-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300",
};

export default function ProductionFilesPortal() {
  const [projects, setProjects] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/production-files/projects")
      .then((r) => setProjects(r.data.projects || []))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setProjects([]); });
  }, []);

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto" data-testid="production-files-portal">
      {selected ? (
        <ProjectFiles code={selected} onBack={() => setSelected(null)} />
      ) : (
        <>
          <div className="mb-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600">Final artwork</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Production Files</h1>
            <p className="text-zinc-500 mt-2 text-sm">Pick a project to download its production-ready files, sub-scope wise.</p>
          </div>
          {projects === null ? (
            <div className="py-16 text-center text-zinc-400">Loading…</div>
          ) : projects.length === 0 ? (
            <div className="py-16 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl"
                 data-testid="production-files-empty">
              No projects with walls yet.
            </div>
          ) : (
            <div className="grid gap-3" data-testid="production-files-project-list">
              {projects.map((p) => (
                <button key={p.project_code} onClick={() => setSelected(p.project_code)}
                        data-testid={`pf-project-${p.project_code}`}
                        className="group text-left bg-white rounded-2xl border border-zinc-200 shadow-sm hover:border-orange-300 hover:shadow transition p-5 flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-50 text-orange-600 shrink-0">
                    <Factory className="w-6 h-6" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-lg font-bold tracking-tight"><ProjRef code={p.project_code} /></div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {p.files} of {p.walls} wall{p.walls !== 1 ? "s" : ""} have a production file
                    </div>
                  </div>
                  <span className={`hidden sm:inline-flex px-2.5 h-6 items-center rounded-full text-[11px] font-medium border
                      ${p.files === p.walls && p.walls > 0
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                    {p.files === p.walls && p.walls > 0 ? "All ready" : `${p.walls - p.files} awaited`}
                  </span>
                  <ChevronRight className="w-5 h-5 text-zinc-300 group-hover:text-orange-500 transition shrink-0" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProjectFiles({ code, onBack }) {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [zipping, setZipping] = useState(false);

  const downloadZip = useCallback(async () => {
    setZipping(true);
    try {
      const r = await api.get(`/production-files/${code}/download-all`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `production-files-${code}.zip`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("All production files downloaded.");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "No production files uploaded yet." : "Zip download failed");
    } finally { setZipping(false); }
  }, [code]);

  useEffect(() => {
    api.get(`/production-files/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed to load"); setData({ groups: [] }); });
  }, [code]);

  const downloadExcel = useCallback(async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/production-files/${code}/export.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `production-files-${code}.xlsx`; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded.");
    } catch { toast.error("Excel download failed"); }
    finally { setDownloading(false); }
  }, [code]);

  return (
    <>
      <button onClick={onBack} data-testid="pf-back"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition mb-4">
        <ArrowLeft className="w-4 h-4" /> All projects
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-orange-600">Production files</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1" data-testid="pf-project-title">{code}</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadZip} disabled={zipping} data-testid="pf-zip-download"
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition disabled:opacity-50">
            <FileDown className="w-4 h-4" /> {zipping ? "Zipping…" : "Download all files"}
          </button>
          <button onClick={downloadExcel} disabled={downloading} data-testid="pf-excel-download"
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition disabled:opacity-50">
            <FileSpreadsheet className="w-4 h-4" /> {downloading ? "Preparing…" : "Download Excel"}
          </button>
        </div>
      </div>

      {data === null ? (
        <div className="py-16 text-center text-zinc-400">Loading…</div>
      ) : (
        <div className="space-y-6" data-testid="pf-groups">
          {(data.groups || []).map(({ group, walls }) => (
            <div key={group}>
              <div className="flex items-center gap-2 mb-2 px-1">
                <FolderOpen className="w-4 h-4 text-orange-600" />
                <span className="font-display font-bold text-zinc-900">{group}</span>
                <span className="text-xs text-zinc-400">· {walls.length} wall{walls.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm divide-y divide-zinc-100 overflow-hidden">
                {walls.map((w) => (
                  <div key={w.boq_id} className="flex items-center gap-3 px-4 sm:px-5 py-3"
                       data-testid={`pf-wall-${w.boq_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-zinc-900 truncate">{w.wall_label}</div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-zinc-500 mt-0.5">
                        {w.measurement && (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                            <Ruler className="w-3.5 h-3.5" /> {w.measurement}
                          </span>
                        )}
                        {(w.area || w.qty) && <span>{w.area || w.qty} {w.unit}</span>}
                        {w.production_file_uploaded_at && (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {fmtIST(w.production_file_uploaded_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {w.production_file_url ? (
                      <>
                        <span className={`hidden sm:inline-flex px-2.5 h-6 items-center rounded-full text-[11px] font-medium border shrink-0
                            ${PF_STATUS[w.production_file_status] || PF_STATUS.pending}`}>
                          {(w.production_file_status || "pending").replace("_", " ")}
                        </span>
                        <a href={fileSrc(w.production_file_url)} target="_blank" rel="noreferrer"
                           download={w.production_file_name || true} data-testid={`pf-download-${w.boq_id}`}
                           className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800 transition shrink-0">
                          <Download className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline max-w-[160px] truncate">{w.production_file_name || "Download"}</span>
                          <span className="sm:hidden">File</span>
                        </a>
                      </>
                    ) : (
                      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 h-6 inline-flex items-center shrink-0"
                            data-testid={`pf-nofile-${w.boq_id}`}>
                        awaiting file
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
